/**
 * ApprovalRulesSection - admin panel for the S5 ApprovalRule family.
 *
 * Renders inside Registration Settings, gated by `registration_rules`.
 * Lists rules in a DataTable with create/edit via ApprovalRuleDialog.
 */

import { useState } from 'react'
import { Plus, Pencil, Trash2, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { ConfirmationDialog } from '@/admin/components/shared/confirmation-dialog'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { FeatureGate } from '@/admin/components/feature-gate'
import { FLAGS } from '@/admin/hooks/use-feature-flag'
import {
  useApprovalRules,
  useDeleteApprovalRule,
  type ApprovalRule,
} from '@/admin/hooks/use-approval-rules'
import type { Severity } from '@/admin/hooks/use-validation-rules'
import { ApprovalRuleDialog } from './approval-rule-dialog'

import { toastApiError } from '@/shared/lib/api-error'
const SEVERITY_VARIANT: Record<Severity, 'destructive' | 'default' | 'secondary'> = {
  BLOCK: 'destructive',
  WARN: 'default',
  INFO: 'secondary',
}

const SEVERITY_LABEL: Record<Severity, string> = {
  BLOCK: 'Block',
  WARN: 'Warn',
  INFO: 'Info',
}

export function ApprovalRulesSection() {
  return (
    <FeatureGate flag={FLAGS.REGISTRATION_RULES}>
      <Section />
    </FeatureGate>
  )
}

function Section() {
  const { data: rules, isLoading } = useApprovalRules()
  const remove = useDeleteApprovalRule()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ApprovalRule | null>(null)
  const [deleting, setDeleting] = useState<ApprovalRule | null>(null)

  function openCreate() {
    setEditing(null)
    setDialogOpen(true)
  }

  function openEdit(rule: ApprovalRule) {
    setEditing(rule)
    setDialogOpen(true)
  }

  async function handleDelete() {
    if (!deleting) return
    try {
      await remove.mutateAsync(deleting.uuid)
      toast.success('Rule deleted')
      setDeleting(null)
    } catch (err) {
      toastApiError(err, 'Failed to delete rule');
    }
  }

  const columns: DataTableColumn<ApprovalRule>[] = [
    {
      id: 'name',
      header: 'Name',
      cell: (row) => (
        <div className="flex flex-col">
          <span className="text-[12px] text-[var(--ink)]">{row.name}</span>
          {row.description && (
            <span className="text-[10px] text-[var(--ink-3)]">{row.description}</span>
          )}
        </div>
      ),
    },
    {
      id: 'severity',
      header: 'Severity',
      cell: (row) => (
        <Badge variant={SEVERITY_VARIANT[row.severity]}>
          {SEVERITY_LABEL[row.severity]}
        </Badge>
      ),
    },
    {
      id: 'target_status',
      header: 'Target',
      cell: (row) => (
        <span className="text-[11px] text-[var(--ink-2)]">
          {row.target_status === 'pending_approval' ? 'Pending approval' : row.target_status}
        </span>
      ),
    },
    {
      id: 'is_active',
      header: 'Status',
      cell: (row) => (
        <Badge variant={row.is_active ? 'default' : 'outline'}>
          {row.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: (row) => (
        <div className="flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="icon-xs"
            type="button"
            onClick={() => openEdit(row)}
            title="Edit rule"
            className="text-[var(--ink-3)]"
          >
            <Pencil size={13} />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            type="button"
            onClick={() => setDeleting(row)}
            title="Delete rule"
            className="text-[var(--ink-3)]"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="mb-8">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <p className="clara-section-label">Approval Rules</p>
          <p className="clara-small mt-1">
            When conditions match at submission time, the registration is held for
            manual approval before it can be issued.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={openCreate}
          className="text-[11px]"
        >
          <Plus size={12} className="mr-1" />
          Add rule
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] py-10">
          <Loader2 className="animate-spin text-[var(--ink-3)]" size={18} />
        </div>
      ) : (rules?.length ?? 0) === 0 ? (
        <div className="rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-6 text-center text-[12px] text-[var(--ink-3)]">
          No approval rules yet. Add one to gate registrations that need
          manual approval (e.g. late registrations beyond 90 days).
        </div>
      ) : (
        <DataTable
          tableId="approval-rules"
          columns={columns}
          data={rules ?? []}
          rowKey="uuid"
          onRowClick={(row) => openEdit(row)}
        />
      )}

      <ApprovalRuleDialog open={dialogOpen} onOpenChange={setDialogOpen} rule={editing} />
      <ConfirmationDialog
        open={!!deleting}
        onOpenChange={(next) => {
          if (!next) setDeleting(null)
        }}
        title={`Delete ${deleting?.name ?? 'rule'}?`}
        description="This removes the approval rule from future registration checks."
        confirmLabel="Delete"
        pendingLabel="Deleting..."
        destructive
        isPending={remove.isPending}
        onConfirm={handleDelete}
      />
    </div>
  )
}
